import React, { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiX, FiBriefcase, FiTrendingUp } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { subscribeClientAccounts } from '../../../lib/entitlements';
import { getAccountOwnerId } from '../utils/ownerEntities';
import './ClientAccountsSubscribe.css';

// =============================================
// Client's Accounts — subscription page (functional mock, no real payment).
// Shown in place of the pillar content until the account owner buys a package.
// Lists site_client_packages; "Subscribe" writes an owner-level subscription row
// (see entitlements.ts) and calls onSubscribed() to unlock the pillar.
// =============================================

interface PackageRow {
  package_id: number;
  package_name: string;
  package_code: string;
  description?: string;
  monthly_price: number;
  annual_price: number;
  currency?: string;
  includes_bookkeeping?: number;
  includes_payroll?: number;
  includes_schedules?: number;
  includes_tax_reports?: number;
  display_order?: number;
  is_visible?: number;
  is_active?: number;
}

const FEATURES: { key: keyof PackageRow; label: string }[] = [
  { key: 'includes_bookkeeping', label: 'Bookkeeping' },
  { key: 'includes_payroll',     label: 'Payroll' },
  { key: 'includes_schedules',   label: 'Recurring schedules' },
  { key: 'includes_tax_reports', label: 'Tax reports' },
];

/** Resolve the logged-in user's account owner id. */
function resolveOwnerId(): number | null {
  return getAccountOwnerId(getTableData('users'));
}

const ClientAccountsSubscribe: React.FC<{ onSubscribed: () => void }> = ({ onSubscribed }) => {
  const [packages, setPackages] = useState<PackageRow[]>(() => getTableData('site_client_packages') as PackageRow[]);
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTable('site_client_packages')
      .then(() => setPackages(getTableData('site_client_packages') as PackageRow[]))
      .catch(() => {});
  }, []);

  const ownerId = useMemo(() => resolveOwnerId(), []);
  const visible = useMemo(
    () => packages
      // eslint-disable-next-line eqeqeq
      .filter(p => (p as any).is_deleted != 1 && p.is_visible != 0 && p.is_active != 0)
      .sort((a, b) => (a.display_order || a.package_id) - (b.display_order || b.package_id)),
    [packages],
  );

  const handleSubscribe = async (pkg: PackageRow) => {
    if (!ownerId) { setError('No account owner found. Please set up your account first.'); return; }
    setBusyId(pkg.package_id);
    setError('');
    try {
      await subscribeClientAccounts(ownerId, pkg.package_id, cycle);
      onSubscribed();
    } catch (e: any) {
      setError(e?.message || 'Could not complete the subscription. Please try again.');
      setBusyId(null);
    }
  };

  return (
    <div className="cas-subscribe">
      <div className="cas-hero">
        <div className="cas-hero-icon"><FiTrendingUp size={36} /></div>
        <h2>Subscribe to manage Client's Accounts</h2>
        <p>
          Run your bookkeeping business inside Akountant. Pick a package to unlock client management —
          add clients, keep each one's books separate, and bill per client. Your own and your family's
          books under <strong>Your Accounts</strong> stay free and unaffected.
        </p>
      </div>

      <div className="cas-cycle-toggle" role="tablist" aria-label="Billing cycle">
        <button role="tab" aria-selected={cycle === 'monthly'} className={cycle === 'monthly' ? 'active' : ''} onClick={() => setCycle('monthly')}>Monthly</button>
        <button role="tab" aria-selected={cycle === 'annual'} className={cycle === 'annual' ? 'active' : ''} onClick={() => setCycle('annual')}>Annual <span className="cas-save">save ~17%</span></button>
      </div>

      {error && <div className="cas-error"><FiX size={16} /> {error}</div>}

      <div className="cas-grid">
        {visible.map(pkg => {
          const price = cycle === 'monthly' ? pkg.monthly_price : pkg.annual_price;
          const unit = cycle === 'monthly' ? '/mo' : '/yr';
          const cur = pkg.currency || 'CAD';
          return (
            <div key={pkg.package_id} className={`cas-card ${pkg.package_code === 'FULL' ? 'cas-card-featured' : ''}`}>
              {pkg.package_code === 'FULL' && <span className="cas-badge">Best value</span>}
              <div className="cas-card-head">
                <FiBriefcase size={18} />
                <h3>{pkg.package_name}</h3>
              </div>
              {pkg.description && <p className="cas-card-desc">{pkg.description}</p>}
              <div className="cas-price">
                <span className="cas-price-amount">{cur} {Number(price).toFixed(2)}</span>
                <span className="cas-price-unit">{unit}</span>
              </div>
              <ul className="cas-features">
                {FEATURES.map(f => {
                  const on = Number(pkg[f.key]) === 1;
                  return (
                    <li key={String(f.key)} className={on ? 'on' : 'off'}>
                      {on ? <FiCheck size={15} /> : <FiX size={15} />} {f.label}
                    </li>
                  );
                })}
              </ul>
              <button
                className="cas-subscribe-btn"
                disabled={busyId !== null}
                onClick={() => handleSubscribe(pkg)}
              >
                {busyId === pkg.package_id ? 'Subscribing…' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      <p className="cas-foot">This is a demo checkout — no payment is taken. You can change or cancel your package later.</p>
    </div>
  );
};

export default ClientAccountsSubscribe;
